import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

function NewBusinesses() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customDays, setCustomDays] = useState(14);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading stats:", error);
      toast.error("Failed to load statistics");
      setLoading(false);
    }
  };

  const triggerScan = async (daysLookback = null) => {
    try {
      const limitResponse = await axios.get(`${API}/subscription/limit-check`);
      const limitInfo = limitResponse.data;

      if (!limitInfo.is_admin && !limitInfo.can_create_lead) {
        toast.error(
          <div>
            <p className="font-semibold">Lead Limit Reached</p>
            <p className="text-sm">You've used {limitInfo.leads_used}/{limitInfo.leads_limit} leads this month.</p>
            <button
              onClick={() => navigate('/plans')}
              className="text-blue-600 underline text-sm mt-1"
            >
              Upgrade your plan →
            </button>
          </div>,
          { duration: 8000 }
        );
        return;
      }

      if (!limitInfo.is_admin && limitInfo.leads_remaining > 0 && limitInfo.leads_remaining <= 5) {
        toast.warning(`You have only ${limitInfo.leads_remaining} leads remaining this month`);
      }
    } catch (error) {
      console.error("Error checking lead limit:", error);
    }

    setScanning(true);
    try {
      const payload = { trigger_type: "manual" };
      if (daysLookback !== null) {
        payload.days_lookback = daysLookback;
      }

      const response = await axios.post(`${API}/scans/trigger`, payload);
      const daysText = daysLookback ? `(${daysLookback} days)` : '';

      toast.success(
        <div>
          <p className="font-semibold">Scan started! {daysText}</p>
          <p className="text-sm">ID: {response.data.scan_id}</p>
          <button
            onClick={() => navigate('/scans')}
            className="text-blue-600 underline text-sm mt-1"
          >
            View Scan History →
          </button>
        </div>,
        { duration: 5000 }
      );

      setTimeout(loadStats, 2000);
    } catch (error) {
      console.error("Error triggering scan:", error);
      const errorMsg = error.response?.data?.detail || "Failed to start scan";
      toast.error(errorMsg);
    } finally {
      setScanning(false);
    }
  };

  const handleCustomScan = () => {
    setShowCustomModal(false);
    triggerScan(customDays);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="new-businesses-page">
      <div className="page-header">
        <h1 className="page-title">New Businesses</h1>
        <p className="page-description">
          Scan the Texas Business Tracker for newly formed businesses, find verified emails, and sync to your CRM
        </p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-scans">
          <div className="stat-label">Total Scans</div>
          <div className="stat-value">{stats?.total_scans || 0}</div>
          <div className="stat-detail">All time</div>
        </div>

        <div className="stat-card" data-testid="stat-businesses">
          <div className="stat-label">Businesses Found</div>
          <div className="stat-value">{stats?.total_businesses || 0}</div>
          <div className="stat-detail">Total discovered</div>
        </div>

        <div className="stat-card" data-testid="stat-emails">
          <div className="stat-label">Emails Found</div>
          <div className="stat-value">{stats?.total_emails || 0}</div>
          <div className="stat-detail">Via Email Discovery</div>
        </div>

        <div className="stat-card" data-testid="stat-synced">
          <div className="stat-label">CRM Synced</div>
          <div className="stat-value">{stats?.total_synced || 0}</div>
          <div className="stat-detail">Contacts created</div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">Quick Scan Options</h2>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            Choose how far back to scan for new Texas business formations. Scans will fetch data from the Texas Open Data Portal, find verified emails, and sync contacts to your CRM.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <Button
              data-testid="scan-7-days-btn"
              onClick={() => triggerScan(7)}
              disabled={scanning}
              className="btn btn-primary"
              style={{ flexDirection: 'column', padding: '16px 12px', height: 'auto' }}
            >
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>7</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Days</div>
            </Button>

            <Button
              data-testid="scan-14-days-btn"
              onClick={() => triggerScan(14)}
              disabled={scanning}
              className="btn btn-primary"
              style={{ flexDirection: 'column', padding: '16px 12px', height: 'auto', background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' }}
            >
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>14</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Days (Default)</div>
            </Button>

            <Button
              data-testid="scan-30-days-btn"
              onClick={() => triggerScan(30)}
              disabled={scanning}
              className="btn btn-primary"
              style={{ flexDirection: 'column', padding: '16px 12px', height: 'auto' }}
            >
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>30</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Days</div>
            </Button>

            <Button
              data-testid="scan-custom-btn"
              onClick={() => setShowCustomModal(true)}
              disabled={scanning}
              className="btn btn-secondary"
              style={{ flexDirection: 'column', padding: '16px 12px', height: 'auto' }}
            >
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>⚙️</div>
              <div style={{ fontSize: '12px' }}>Custom</div>
            </Button>
          </div>

          {scanning && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: '#eff6ff', borderRadius: '8px', fontSize: '14px', color: '#1e40af' }}>
              <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
              <span>Scanning in progress...</span>
            </div>
          )}
        </div>

        {showCustomModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCustomModal(false)}>
            <div style={{ background: 'white', padding: '32px', borderRadius: '12px', maxWidth: '400px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#0f172a' }}>
                Custom Scan Period
              </h3>
              <div className="form-group">
                <label className="form-label">Days to look back (1-90)</label>
                <input
                  type="number"
                  className="form-input"
                  value={customDays}
                  onChange={(e) => setCustomDays(parseInt(e.target.value) || 14)}
                  min="1"
                  max="90"
                  data-testid="custom-days-input"
                />
                <p className="form-help">
                  Enter the number of days to scan for new business formations
                </p>
              </div>
              <div className="btn-group" style={{ marginTop: '20px' }}>
                <Button onClick={() => setShowCustomModal(false)} className="btn btn-secondary">
                  Cancel
                </Button>
                <Button onClick={handleCustomScan} className="btn btn-primary" data-testid="custom-scan-start-btn">
                  Start Scan ({customDays} days)
                </Button>
              </div>
            </div>
          </div>
        )}

        {stats?.latest_scan && (
          <div style={{ marginTop: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
              Latest Scan
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '13px' }}>
              <div>
                <span style={{ color: '#64748b' }}>Status:</span>{' '}
                <span className={`status-badge status-${stats.latest_scan.status}`}>
                  {stats.latest_scan.status}
                </span>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Businesses:</span>{' '}
                <strong>{stats.latest_scan.businesses_found || 0}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Emails:</span>{' '}
                <strong>{stats.latest_scan.emails_found || 0}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b' }}>Synced:</span>{' '}
                <strong>{stats.latest_scan.contacts_synced || 0}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NewBusinesses;
